import useToast from '@/hooks/use-toast.hook';
import { patchOrg } from '@/lib/services/org.service';
import { Box, Stack, Theme } from '@mui/material';
import { FC, useState } from 'react';
import { FileWithPath } from 'react-dropzone';
import ImageUploader from '@/components/image-uploader';
import { ImageExtension } from '@/components/image-uploader/helpers';
import ImageUploadPreviewCard from '@/components/image-uploader/preview-card';
import ExternalLink from '@/components/external-link';
import { createUseStyles } from 'react-jss';
import { OrgModel } from '@/lib/models/org/org.model';
import { OrgBrandingTheme } from '@/lib/models/org';
import BrandingThemeTabs from './branding-theme-tabs';
import useFeature from '@/hooks/use-feature.hook';
import FormSectionSubtitle from '@/components/form/form-section/form-section-subtitle';

export type FileWithPreview = FileWithPath & { preview: string };

interface Props {
  organization: OrgModel;
  readonly: boolean;
  onUpload?: () => void;
}

const MAX_FILE_SIZE_KB = 300;
const IMAGE_WIDTH_PX = 250;
const IMAGE_HEIGHT_PX = 40;
const ALLOWED_IMAGE_TYPES: ImageExtension[] = ['png', 'jpg'];

const useStyles = createUseStyles<string, { mode: OrgBrandingTheme }>((theme: Theme) => ({
  uploadPreviewCard: {
    '& .MuiPaper-root': {
      backgroundColor: ({ mode }) =>
        mode === 'dark'
          ? theme.palette.portal.background.dark
          : theme.palette.portal.background.light,
    },
    '& .MuiPaper-root, & .MuiChip-label': {
      color: ({ mode }) =>
        mode === 'dark' ? theme.palette.common.white : theme.palette.common.black,
    },
  },
}));

const OrgLogoUploadForm: FC<Props> = ({ organization, readonly, onUpload }) => {
  const { toast, errorToast } = useToast();
  const { hasPortalChat } = useFeature();
  const [image, setImage] = useState<{ light: string; dark: string }>({
    light: organization.branding.lightThemeLogo,
    dark: organization.branding.darkThemeLogo,
  });
  const [mode, setMode] = useState<OrgBrandingTheme>('light');
  const isDarkMode = mode === 'dark';

  const styles = useStyles({ mode });

  const handleUploadImage = (dataURI: string) => {
    const updatedOrg = new OrgModel(organization._props);

    if (isDarkMode) {
      updatedOrg.branding.darkThemeLogo = dataURI;
    } else {
      updatedOrg.branding.lightThemeLogo = dataURI;
    }

    patchOrg(updatedOrg).then(({ patched, error }) => {
      if (patched) {
        onUpload?.();
        setImage((old) => {
          return {
            ...old,
            [mode]: dataURI,
          };
        });
        toast(`The organization ${mode} theme logo was updated`);
        return;
      }

      errorToast(error || `The organization ${mode} theme logo was not updated`);
    });
  };

  const handleRemoveImage = () => {
    const updatedOrg = new OrgModel(organization._props);

    if (isDarkMode) {
      updatedOrg.branding.darkThemeLogo = '';
    } else {
      updatedOrg.branding.lightThemeLogo = '';
    }

    patchOrg(updatedOrg).then(({ patched, error }) => {
      if (patched) {
        onUpload?.();
        setImage((old) => {
          return {
            ...old,
            [mode]: '',
          };
        });
        toast(`The organization ${mode} theme logo was removed`);
        return;
      }

      errorToast(error || `The organization ${mode} theme logo was not removed`);
    });
  };

  const handleChangeMode = (tabPath: string) => {
    setMode(tabPath as OrgBrandingTheme);
  };

  return (
    <>
      <FormSectionSubtitle>
        {hasPortalChat ? (
          <>
            The Organization Logo will appear in{' '}
            <ExternalLink
              inline
              withIcon={false}
              href="https://portal.surepath.ai"
              label="portal.surepath.ai"
            />{' '}
            at the top of the sidebar, and as part of the Portal Chat Welcome Message. It will also
            appear in page loaders and in the page header of{' '}
            <ExternalLink
              inline
              withIcon={false}
              href="https://auth.surepath.ai"
              label="auth.surepath.ai"
            />
            .
          </>
        ) : (
          <>
            The Organization Logo will appear in{' '}
            <ExternalLink
              inline
              withIcon={false}
              href="https://portal.surepath.ai"
              label="portal.surepath.ai"
            />{' '}
            at the top of the sidebar, It will also appear in page loaders and in the page header of{' '}
            <ExternalLink
              inline
              withIcon={false}
              href="https://auth.surepath.ai"
              label="auth.surepath.ai"
            />
            .
          </>
        )}
      </FormSectionSubtitle>
      <BrandingThemeTabs onChange={handleChangeMode} />
      <Stack direction="row" gap={6} width="100%">
        {!readonly && (
          <Box width="50%">
            <ImageUploader
              title="Upload New Organization Logo"
              onUpload={handleUploadImage}
              maxFileSizeKb={MAX_FILE_SIZE_KB}
              imageHeight={IMAGE_HEIGHT_PX}
              imageWidth={IMAGE_WIDTH_PX}
              allowedImageTypes={ALLOWED_IMAGE_TYPES}
            />
          </Box>
        )}
        <Box width="50%">
          <ImageUploadPreviewCard
            imageHeight={IMAGE_HEIGHT_PX}
            imageWidth={IMAGE_WIDTH_PX}
            maxFileSize={MAX_FILE_SIZE_KB}
            allowedImageTypes={ALLOWED_IMAGE_TYPES}
            title="Current Organization Logo"
            onRemove={handleRemoveImage}
            readonly={readonly}
            image={image[mode]}
            className={styles.uploadPreviewCard}
          />
        </Box>
      </Stack>
    </>
  );
};

export default OrgLogoUploadForm;
